import { useState, useEffect, useCallback, useRef } from 'react';

export function useSearch({
  backendUrl,
  propChannelUrl,
  propCustomerKey,
  signedToken = '',
  showDemoSelector = false,
  onSearchStart = () => {},
  onSearchEnd = () => {},
}) {
  const [queryResults, setQueryResults] = useState([]);
  const [matchContexts, setMatchContexts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastChannelUrl, setLastChannelUrl] = useState('');
  const [lastQueryPhrase, setLastQueryPhrase] = useState('');
  const [lastCustomerKey, setLastCustomerKey] = useState('');
  const [lastSiteId, setLastSiteId] = useState('');
  const [dateFilter, setDateFilter] = useState({ preset: 'all', dateFrom: null, dateTo: null });

  // Track whether a search has been performed (so date filter changes can auto-re-search)
  const hasSearched = useRef(false);

  const [selectedDemo, setSelectedDemo] = useState('');
  const [demoConfigs, setDemoConfigs] = useState({});
  const [demoConfigsError, setDemoConfigsError] = useState(false);
  const [channelUrl, setChannelUrl] = useState(propChannelUrl || '');
  const [customerKey, setCustomerKey] = useState(propCustomerKey || '');

  useEffect(() => {
    setChannelUrl(propChannelUrl || '');
    setCustomerKey(propCustomerKey || '');
  }, [propChannelUrl, propCustomerKey]);

  useEffect(() => {
    if (!showDemoSelector) return;

    const fetchDemoConfigs = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/demo-configs`);
        if (response.ok) {
          const configs = await response.json();
          setDemoConfigs(configs);
          setDemoConfigsError(false);
        } else {
          console.error('Failed to fetch demo configs:', response.status);
          setDemoConfigsError(true);
        }
      } catch (error) {
        console.error('Error fetching demo configs:', error);
        setDemoConfigsError(true);
      }
    };

    fetchDemoConfigs();
  }, [backendUrl, showDemoSelector]);

  const handleDemoSelection = useCallback((demoKey) => {
    if (selectedDemo === demoKey) {
      setSelectedDemo('');
      setChannelUrl(propChannelUrl || '');
      setCustomerKey(propCustomerKey || '');
    } else {
      setSelectedDemo(demoKey);
      const config = demoConfigs[demoKey];
      if (config) {
        // Demo configs are credential-free: channel_url is for display and
        // result grouping only; auth happens per-search by trading the
        // config's site_id for a short-lived token via /api/widget-token.
        setChannelUrl(config.channel_url);
        setCustomerKey('');
      }
    }
  }, [selectedDemo, demoConfigs, propChannelUrl, propCustomerKey]);

  // Mint a fresh, origin-scoped widget token via POST (POST sends the Origin
  // header even same-origin; GET does not). Minted per-search, like useAsk
  // does per-ask, so an expired page-load token (15-min TTL) can never cause
  // a 401 mid-session.
  const mintToken = useCallback(async (siteId) => {
    const r = await fetch(`${backendUrl}/api/widget-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site_id: siteId }),
    });
    if (!r.ok) throw new Error('token ' + r.status);
    return (await r.json()).token;
  }, [backendUrl]);

  const formatTimestamp = useCallback((seconds) => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getDisplayText = useCallback((result) => {
    if (result.metadata?.text_short) return result.metadata.text_short;
    const text = result.metadata?.text || result.text || 'Not available';
    if (text.length <= 150) return text;
    const truncated = text.substring(0, 147);
    const lastSpace = truncated.lastIndexOf(' ');
    return (lastSpace > 0 ? text.substring(0, lastSpace) : truncated) + '...';
  }, []);

  const getActionProps = useCallback((result, videoGroup) => {
    const videoId = videoGroup.video_id;
    const queryKey = `queryResults_${lastChannelUrl}_${lastQueryPhrase}`;

    if (result.metadata?.source_type === 'pdf') {
      const params = new URLSearchParams();
      params.set('pdf_id', result.metadata.pdf_id);
      params.set('page', result.metadata.page_number);
      params.set('queryKey', queryKey);
      // Demo searches carry the public site_id; the landing page trades it
      // for a widget token. Credentialed searches keep passing the key.
      if (lastSiteId) params.set('site_id', lastSiteId);
      if (lastCustomerKey) params.set('customer_key', lastCustomerKey);
      if (lastChannelUrl) params.set('channel_url', lastChannelUrl);
      return {
        url: `${backendUrl}/${videoId}?${params.toString()}`,
        icon: '📄',
        type: 'pdf',
        pageNumber: result.metadata.page_number,
      };
    }

    const url = result.metadata?.url;
    const youtubeMatch = url?.match(/[?&]t=(\d+)/);
    const vimeoMatch = url?.match(/#t=(\d+)/);

    if (youtubeMatch || vimeoMatch) {
      const t = parseInt(youtubeMatch ? youtubeMatch[1] : vimeoMatch[1]);
      const params = new URLSearchParams();
      params.set('t', t);
      params.set('queryKey', queryKey);
      return {
        url: `${backendUrl}/${videoId}?${params.toString()}`,
        icon: '▶',
        type: 'video',
        timestamp: t,
      };
    }

    const params = new URLSearchParams();
    params.set('queryKey', queryKey);
    return {
      url: `${backendUrl}/${videoId}?${params.toString()}`,
      icon: '▶',
      type: 'video',
      timestamp: null,
    };
  }, [backendUrl, lastChannelUrl, lastQueryPhrase, lastCustomerKey, lastSiteId]);

  const buildMatchContexts = (results, transcriptMap) => {
    const contexts = {};
    results.forEach(group => {
      if (!group.video_id || !transcriptMap[group.video_id]) return;
      const transcript = transcriptMap[group.video_id];
      (group.matches || []).forEach(match => {
        if (match.metadata?.source_type === 'pdf') return;
        const matchStart = match.metadata?.start;
        if (matchStart == null) return;

        const segIdx = transcript.findIndex(seg =>
          Math.abs(seg.start - matchStart) < 1
        );
        if (segIdx === -1) return;

        const beforeSegs = transcript.slice(Math.max(0, segIdx - 3), segIdx);
        const afterSegs = transcript.slice(segIdx + 1, segIdx + 4);

        contexts[`${group.video_id}_${matchStart}`] = {
          before: beforeSegs.map(s => s.text).join(' '),
          after: afterSegs.map(s => s.text).join(' '),
        };
      });
    });
    return contexts;
  };

  const executeSearch = useCallback(async (queryPhrase, searchChannelUrl, searchCustomerKey, filter, searchSiteId = '') => {
    if (!queryPhrase || (!signedToken && !searchSiteId && (!searchChannelUrl || !searchCustomerKey))) return;

    setIsLoading(true);
    onSearchStart({ queryPhrase, channelUrl: searchChannelUrl, customerKey: searchCustomerKey });

    try {
      let queryUrl;
      if (signedToken) {
        queryUrl = `${backendUrl}/api/query?query_phrase=${encodeURIComponent(queryPhrase)}&signed_token=${encodeURIComponent(signedToken)}`;
      } else if (searchSiteId) {
        // Demo flow: /api/demo-configs no longer exposes customer keys, so
        // trade the public site_id for a short-lived origin-gated token.
        let demoToken;
        try {
          demoToken = await mintToken(searchSiteId);
        } catch (e) {
          console.error('Demo token mint failed:', e);
          const errorResults = [{ error: 'This demo is unavailable right now. Please try another.' }];
          setQueryResults(errorResults);
          onSearchEnd({ error: 'Demo token mint failed', results: errorResults });
          return;
        }
        queryUrl = `${backendUrl}/api/query?query_phrase=${encodeURIComponent(queryPhrase)}&signed_token=${encodeURIComponent(demoToken)}`;
      } else {
        queryUrl = `${backendUrl}/api/query?query_phrase=${encodeURIComponent(queryPhrase)}&channel_url=${encodeURIComponent(searchChannelUrl)}&customer_key=${encodeURIComponent(searchCustomerKey)}`;
      }
      if (filter?.dateFrom) queryUrl += `&date_from=${filter.dateFrom}`;
      if (filter?.dateTo) queryUrl += `&date_to=${filter.dateTo}`;
      const response = await fetch(queryUrl, { credentials: 'include' });

      if (response.status === 401) {
        alert('You are not authenticated. Please check your credentials.');
        setQueryResults([{ error: 'Authentication failed. Please check your credentials.' }]);
        onSearchEnd({ error: 'Authentication failed', results: [] });
        return;
      }

      const results = await response.json();
      if (!results || results.length === 0) {
        setQueryResults(results || []);
        setMatchContexts({});
        setLastChannelUrl(searchChannelUrl);
        setLastQueryPhrase(queryPhrase);
        setLastCustomerKey(searchCustomerKey);
        setLastSiteId(searchSiteId);
        hasSearched.current = true;
        onSearchEnd({ results: results || [], queryPhrase, channelUrl: searchChannelUrl, customerKey: searchCustomerKey });
        return;
      }

      // Blocking: fetch transcripts for all unique video IDs
      const videoIds = [...new Set(
        results.filter(group => group.video_id).map(group => group.video_id)
      )];

      const transcriptMap = {};
      if (videoIds.length > 0) {
        await Promise.all(videoIds.map(async (videoId) => {
          try {
            const baseVideoId = videoId.split('/')[0];
            const res = await fetch(`${backendUrl}/api/transcript?video_id=${baseVideoId}`);
            if (res.ok) {
              transcriptMap[videoId] = await res.json();
            }
          } catch {
            // Silently fail — results display without context for this video
          }
        }));
      }

      const contexts = buildMatchContexts(results, transcriptMap);

      // Single batch update — React 18 auto-batches these
      setQueryResults(results);
      setMatchContexts(contexts);
      setLastChannelUrl(searchChannelUrl);
      setLastQueryPhrase(queryPhrase);
      setLastCustomerKey(searchCustomerKey);
      setLastSiteId(searchSiteId);
      hasSearched.current = true;
      onSearchEnd({ results, queryPhrase, channelUrl: searchChannelUrl, customerKey: searchCustomerKey });
    } catch (error) {
      console.error('Search error:', error);
      const errorResults = [{ error: 'Error occurred during query. Please try again.' }];
      setQueryResults(errorResults);
      onSearchEnd({ error: error.message, results: errorResults });
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, signedToken, mintToken, onSearchStart, onSearchEnd]);

  const handleQuerySubmit = async (event) => {
    event.preventDefault();
    const fields = event.target.elements || event.target;
    const queryPhrase = fields.queryPhrase?.value || '';

    const demoSiteId = (selectedDemo && demoConfigs[selectedDemo]?.site_id) || '';
    const finalChannelUrl = channelUrl || fields.channelURL?.value;
    const finalCustomerKey = customerKey || fields.customerKey?.value;

    if (!signedToken && !demoSiteId && (!finalChannelUrl || !finalCustomerKey)) {
      alert('Please provide both channel URL and customer key.');
      return;
    }

    if (!queryPhrase.trim()) {
      alert('Please enter a search query.');
      return;
    }

    await executeSearch(queryPhrase, finalChannelUrl, finalCustomerKey, dateFilter, demoSiteId);
  };

  // Auto-re-search when date filter changes (only if a search was already performed).
  // Re-searches the tenant of the last search (lastSiteId), not the currently
  // selected pill, so switching demos before searching can't cross corpora.
  useEffect(() => {
    if (!hasSearched.current) return;
    if (!lastQueryPhrase || (!signedToken && !lastSiteId && (!lastChannelUrl || !lastCustomerKey))) return;
    executeSearch(lastQueryPhrase, lastChannelUrl, lastCustomerKey, dateFilter, lastSiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  return {
    queryResults,
    matchContexts,
    isLoading,
    lastChannelUrl,
    lastQueryPhrase,
    dateFilter,
    setDateFilter,
    demoConfigs,
    demoConfigsError,
    selectedDemo,
    channelUrl,
    customerKey,
    setChannelUrl,
    setCustomerKey,
    handleQuerySubmit,
    handleDemoSelection,
    formatTimestamp,
    getDisplayText,
    getActionProps,
  };
}
